import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { 
  FileText, Upload, Download, Trash2, Loader2, Search,
  BookOpen, User, Calendar, Filter, BarChart3, Plus, X, Eye, Check, Layers, 
  Palette, PenTool, Archive, AlertTriangle
} from 'lucide-react';

const ClassTyping = ({ initialTab = 'dashboard' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine active tab from URL
  const getTabFromPath = () => {
    if (location.pathname.includes('/repository')) return 'repository';
    return 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromPath());
  const [loading, setLoading] = useState(true);
  const [ppts, setPpts] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // Dashboard date-range selection with quick presets
  const todayISO = () => new Date().toISOString().split('T')[0];
  const shiftDate = (d, days) => {
    const t = new Date(d);
    t.setDate(t.getDate() + days);
    return t.toISOString().split('T')[0];
  };
  const monthStart = (d) => {
    const t = new Date(d);
    return new Date(t.getFullYear(), t.getMonth(), 1).toISOString().split('T')[0];
  };
  const monthEnd = (d) => {
    const t = new Date(d);
    return new Date(t.getFullYear(), t.getMonth() + 1, 0).toISOString().split('T')[0];
  };
  const RANGE_PRESETS = [
    { key: 'today',      label: 'Today',       compute: () => { const t = todayISO(); return { start: t, end: t }; } },
    { key: 'yesterday',  label: 'Yesterday',   compute: () => { const y = shiftDate(todayISO(), -1); return { start: y, end: y }; } },
    { key: 'last_7',     label: 'Last 7 Days', compute: () => ({ start: shiftDate(todayISO(), -6), end: todayISO() }) },
    { key: 'this_month', label: 'This Month',  compute: () => ({ start: monthStart(todayISO()), end: todayISO() }) },
    { key: 'last_month', label: 'Last Month',  compute: () => {
        const prev = new Date();
        prev.setDate(1);
        prev.setMonth(prev.getMonth() - 1);
        const iso = prev.toISOString().split('T')[0];
        return { start: monthStart(iso), end: monthEnd(iso) };
      } },
    { key: 'custom',     label: 'Custom' },
  ];
  const [dashboardRange, setDashboardRange] = useState(() => {
    const t = todayISO();
    return { start: t, end: t, key: 'today' };
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  
  // Topic autocomplete
  const [topicSearch, setTopicSearch] = useState('');
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [addingTopic, setAddingTopic] = useState(false);
  const topicInputRef = useRef(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterTeacher, setFilterTeacher] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    title: '',
    subject_id: '',
    teacher_id: '',
    topic: '',
    description: '',
    file: null
  });
  
  // Reupload
  const [reuploadPpt, setReuploadPpt] = useState(null);
  const [reuploadFile, setReuploadFile] = useState(null);
  const [reuploading, setReuploading] = useState(false);
  
  // Get current user
  const user = authService.getCurrentUser();
  const isManager = user?.role === 'admin' || user?.role === 'typing_manager' || user?.role === 'manager';
  const isAgent = user?.role === 'typing_agent';
  const isTeacher = user?.role === 'teacher';

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath());
  }, [location.pathname]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'repository') {
      fetchPPTs();
    }
  }, [searchQuery, filterSubject, filterTeacher, startDate, endDate, currentPage, activeTab]);
  
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboardStats(dashboardRange);
    }
  }, [activeTab, dashboardRange]);

  const fetchDashboardStats = async (range) => {
    try {
      const response = await api.getTypingDashboardStats({
        start_date: range.start, end_date: range.end,
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subjectsRes, teachersRes, topicsRes] = await Promise.all([
        api.getTypingSubjects(),
        api.getTypingTeachers(),
        api.getTypingTopics()
      ]);
      setSubjects(subjectsRes.data || []);
      setTeachers(teachersRes.data || []);
      setTopics(topicsRes.data || []);
      
      // Fetch stats for managers and agents
      if (isManager || isAgent) {
        try {
          const [statsRes, dashRes] = await Promise.all([
            api.getTypingStats(),
            api.getTypingDashboardStats({ start_date: dashboardRange.start, end_date: dashboardRange.end })
          ]);
          setStats(statsRes.data);
          setDashboardStats(dashRes.data);
        } catch (e) {
          console.log('Stats not available');
        }
      }
      
      if (activeTab === 'repository') {
        await fetchPPTs();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async (subjectId = null) => {
    try {
      const params = subjectId ? { subject_id: subjectId } : {};
      const response = await api.getTypingTopics(params);
      setTopics(response.data || []);
    } catch (error) {
      console.error('Error fetching topics:', error);
    }
  };

  // Filter topics based on search input
  useEffect(() => {
    if (topicSearch.trim()) {
      const filtered = topics.filter(t => 
        t.name.toLowerCase().includes(topicSearch.toLowerCase())
      );
      setFilteredTopics(filtered);
    } else {
      setFilteredTopics(topics);
    }
  }, [topicSearch, topics]);

  // Fetch topics when subject changes in upload form
  useEffect(() => {
    if (uploadForm.subject_id) {
      fetchTopics(uploadForm.subject_id);
    }
  }, [uploadForm.subject_id]);

  const handleTopicSelect = (topic) => {
    setUploadForm(prev => ({ ...prev, topic: topic.name }));
    setTopicSearch(topic.name);
    setShowTopicDropdown(false);
  };

  const handleAddNewTopic = async () => {
    if (!topicSearch.trim()) return;
    
    setAddingTopic(true);
    try {
      const response = await api.createTypingTopic({
        name: topicSearch.trim(),
        subject_id: uploadForm.subject_id || null
      });
      
      if (response.data.created) {
        toast.success('Topic added to library');
        setTopics(prev => [...prev, response.data.topic]);
      } else {
        toast.info('Topic already exists');
      }
      
      setUploadForm(prev => ({ ...prev, topic: topicSearch.trim() }));
      setShowTopicDropdown(false);
    } catch (error) {
      toast.error('Failed to add topic');
    } finally {
      setAddingTopic(false);
    }
  };

  const fetchPPTs = async () => {
    try {
      const params = {
        page: currentPage,
        limit: 20
      };
      if (searchQuery) params.search = searchQuery;
      if (filterSubject) params.subject_id = filterSubject;
      if (filterTeacher) params.teacher_id = filterTeacher;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await api.getTypingPPTs(params);
      const data = response.data;
      
      // Handle both old format (array) and new format (object with ppts and pagination)
      if (Array.isArray(data)) {
        setPpts(data);
        setPagination(null);
      } else {
        setPpts(data.ppts || []);
        setPagination(data.pagination || null);
      }
    } catch (error) {
      console.error('Error fetching PPTs:', error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }
    if (!uploadForm.title || !uploadForm.subject_id || !uploadForm.teacher_id) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!uploadForm.topic || !uploadForm.topic.trim()) {
      toast.error('Topic is required');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('subject_id', uploadForm.subject_id);
      formData.append('teacher_id', uploadForm.teacher_id);
      formData.append('topic', uploadForm.topic.trim());
      formData.append('description', uploadForm.description || '');
      formData.append('file', uploadForm.file);
      
      const response = await api.uploadTypingPPT(formData);
      toast.success(response.data.message || 'PPT uploaded successfully');
      
      // Reset form
      setUploadForm({
        title: '',
        subject_id: '',
        teacher_id: '',
        topic: '',
        description: '',
        file: null
      });
      setTopicSearch('');
      setShowUploadModal(false);
      fetchPPTs();
      fetchTopics(); // Refresh topics in case new one was added
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (ppt) => {
    try {
      const response = await api.downloadTypingPPT(ppt.id);
      
      // Check if response is an error (JSON) instead of blob
      if (response.data instanceof Blob && response.data.type.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Download failed');
      }
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ppt.original_filename || `${ppt.title}.pptx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      let errorMsg = 'Download failed';
      let showReuploadOption = false;
      
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMsg = errorData.detail || errorMsg;
          // Check if error indicates file not found
          if (errorMsg.toLowerCase().includes('not found') || errorMsg.toLowerCase().includes('reupload')) {
            showReuploadOption = true;
          }
        } catch (e) {
          // If parsing fails, use default message
        }
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
        if (errorMsg.toLowerCase().includes('not found') || errorMsg.toLowerCase().includes('reupload')) {
          showReuploadOption = true;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      if (showReuploadOption) {
        toast.error(
          <div>
            <p>{errorMsg}</p>
            <button 
              onClick={() => { setReuploadPpt(ppt); toast.dismiss(); }}
              className="mt-2 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
            >
              Reupload PPT
            </button>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleReupload = async () => {
    if (!reuploadPpt || !reuploadFile) {
      toast.error('Please select a file to reupload');
      return;
    }
    
    setReuploading(true);
    try {
      const formData = new FormData();
      formData.append('file', reuploadFile);
      
      await api.reuploadTypingPPT(reuploadPpt.id, formData);
      toast.success('PPT reuploaded successfully!');
      setReuploadPpt(null);
      setReuploadFile(null);
      fetchPPTs();
    } catch (error) {
      console.error('Reupload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reupload PPT');
    } finally {
      setReuploading(false);
    }
  };

  const handleDelete = async (pptId) => {
    if (!window.confirm('Are you sure you want to delete this PPT?')) return;
    
    setDeleting(pptId);
    try {
      await api.deleteTypingPPT(pptId);
      toast.success('PPT deleted');
      fetchPPTs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-headings uppercase tracking-tight text-slate-900">
            {isTeacher ? 'My PPTs' : 'Typing Module'}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 font-body mt-1">
            {isTeacher 
              ? 'PPT presentations assigned to you' 
              : isAgent 
                ? 'Upload PPTs for teachers' 
                : 'Manage PPT uploads and assignments'}
          </p>
        </div>

        {(isAgent || isManager) && activeTab === 'repository' && (
          <button
            data-testid="upload-ppt-btn"
            onClick={() => setShowUploadModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm font-body w-full sm:w-auto"
          >
            <Upload size={18} />
            <span>Upload PPT</span>
          </button>
        )}
      </div>

      {/* Dashboard Tab Content */}
      {activeTab === 'dashboard' && isManager && dashboardStats && (
        <div className="space-y-6">
          {/* Date Range Selection */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="typing-dashboard-range">
            <div className="flex flex-wrap items-center gap-2">
              <Calendar size={16} className="text-slate-500" />
              <span className="text-sm font-medium text-slate-700 mr-1">Date Range:</span>
              {RANGE_PRESETS.map((p) => {
                const isActive = dashboardRange.key === p.key;
                return (
                  <button
                    key={p.key}
                    data-testid={`range-preset-${p.key}`}
                    onClick={() => {
                      if (p.key === 'custom') {
                        setDashboardRange((prev) => ({ ...prev, key: 'custom' }));
                      } else {
                        const r = p.compute();
                        setDashboardRange({ ...r, key: p.key });
                      }
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            {dashboardRange.key === 'custom' && (
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <label className="text-xs text-slate-600">From</label>
                <input
                  type="date"
                  data-testid="range-custom-start"
                  value={dashboardRange.start}
                  max={dashboardRange.end || todayISO()}
                  onChange={(e) => setDashboardRange((prev) => ({ ...prev, start: e.target.value, key: 'custom' }))}
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <label className="text-xs text-slate-600">To</label>
                <input
                  type="date"
                  data-testid="range-custom-end"
                  value={dashboardRange.end}
                  min={dashboardRange.start}
                  max={todayISO()}
                  onChange={(e) => setDashboardRange((prev) => ({ ...prev, end: e.target.value, key: 'custom' }))}
                  className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-5 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-lg">
                  <FileText size={24} />
                </div>
                <div>
                  <p className="text-sm text-blue-100">PPTs Uploaded</p>
                  <p className="text-3xl font-bold">{dashboardStats.stats?.ppts_uploaded || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-sm p-5 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-lg">
                  <Layers size={24} />
                </div>
                <div>
                  <p className="text-sm text-green-100">Total Size</p>
                  <p className="text-3xl font-bold">{dashboardStats.stats?.total_size_formatted || '0 B'}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-sm p-5 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-lg">
                  <User size={24} />
                </div>
                <div>
                  <p className="text-sm text-purple-100">Active Typing Agents</p>
                  <p className="text-3xl font-bold">{dashboardStats.all_time?.total_agents || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Best Performing Typists */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h2 className="text-lg font-semibold text-slate-900">
                Best Performing Typists
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {(() => {
                  const label = RANGE_PRESETS.find((p) => p.key === dashboardRange.key)?.label || 'Range';
                  const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                  const range = dashboardRange.start === dashboardRange.end
                    ? fmt(dashboardRange.start)
                    : `${fmt(dashboardRange.start)} → ${fmt(dashboardRange.end)}`;
                  return `Sorted by number of PPTs uploaded · ${label} (${range})`;
                })()}
              </p>
            </div>
            
            {dashboardStats.typist_performance?.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {dashboardStats.typist_performance.map((typist, index) => (
                  <div key={typist.id || index} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        index === 0 ? 'bg-yellow-500' : 
                        index === 1 ? 'bg-slate-400' : 
                        index === 2 ? 'bg-amber-600' : 'bg-slate-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{typist.name}</p>
                        <p className="text-sm text-slate-500">
                          {(typist.total_size / (1024 * 1024)).toFixed(1)} MB total
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">{typist.ppts_count}</p>
                      <p className="text-sm text-slate-500">PPTs</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-12 text-center text-slate-500">
                <FileText className="mx-auto mb-3 text-slate-300" size={48} />
                <p className="text-lg font-medium">No uploads in this range yet</p>
                <p className="text-sm mt-1">PPT uploads will appear here</p>
              </div>
            )}
          </div>

          {/* All Time Stats Summary */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">All Time Summary</h3>
            <div className="flex gap-8">
              <div>
                <p className="text-sm text-slate-500">Total PPTs</p>
                <p className="text-2xl font-bold text-slate-900">{dashboardStats.all_time?.total_ppts || 0}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Agents</p>
                <p className="text-2xl font-bold text-slate-900">{dashboardStats.all_time?.total_agents || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render content based on active tab from URL */}
      {(activeTab === 'repository' || isAgent || isTeacher) && (
        <>
          {/* Stats Cards - For Typing Agents only in repository view */}
          {isAgent && stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-body">My Uploads</p>
                    <p className="text-2xl font-bold text-slate-900">{ppts.length}</p>
                  </div>
                </div>
              </div>
          
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <BookOpen className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-body">Subjects</p>
                <p className="text-2xl font-bold text-slate-900">
                  {[...new Set(ppts.map(p => p.subject_name))].filter(Boolean).length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <User className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-body">Teachers</p>
                <p className="text-2xl font-bold text-slate-900">
                  {[...new Set(ppts.map(p => p.teacher_name))].filter(Boolean).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters - For Managers and Typing Agents */}
      {(isManager || isAgent) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex flex-col gap-3">
            {/* First row: Search and dropdowns */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Search - Full width on mobile */}
              <div className="w-full sm:flex-1 sm:min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search PPTs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    data-testid="search-ppts"
                  />
                </div>
              </div>
              
              {/* Filters - Stack on mobile */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="hidden sm:flex items-center">
                  <Filter size={18} className="text-slate-400 mr-2" />
                </div>
                <select
                  value={filterSubject}
                  onChange={(e) => setFilterSubject(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="filter-subject"
                >
                  <option value="">All Subjects</option>
                  {subjects.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                
                <select
                  value={filterTeacher}
                  onChange={(e) => setFilterTeacher(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="filter-teacher"
                >
                  <option value="">All Teachers</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Second row: Date filters */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-slate-100">
              <div className="flex items-center">
                <Calendar size={18} className="text-slate-400 mr-2" />
                <span className="text-sm text-slate-600 mr-2">Date Range:</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 flex-1">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="filter-start-date"
                  placeholder="Start Date"
                />
                <span className="hidden sm:flex items-center text-slate-400">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="filter-end-date"
                  placeholder="End Date"
                />
                {(startDate || endDate) && (
                  <button
                    onClick={() => { setStartDate(''); setEndDate(''); }}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Clear Dates
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PPT List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-900 font-headings">
            {isTeacher ? 'Your PPTs' : isAgent ? 'My Uploads' : 'All PPTs'}
          </h2>
        </div>

        {ppts.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500">
            <FileText className="mx-auto mb-3 text-slate-300" size={48} />
            <p className="text-lg font-medium">No PPTs found</p>
            <p className="text-sm mt-1">
              {isTeacher 
                ? 'No PPTs have been assigned to you yet' 
                : 'Upload your first PPT to get started'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {/* Unavailable PPTs Warning Banner */}
            {ppts.some(ppt => ppt.needs_reupload) && (
              <div className="px-6 py-3 bg-red-50 border-b border-red-200">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle size={18} />
                  <span className="font-medium">
                    {ppts.filter(p => p.needs_reupload).length} PPT(s) need to be reuploaded
                  </span>
                  <span className="text-sm text-red-600 ml-2">
                    - Files highlighted in red are not available for download
                  </span>
                </div>
              </div>
            )}
            {ppts.map((ppt) => (
              <div 
                key={ppt.id}
                className={`px-4 sm:px-6 py-4 transition-colors ${
                  ppt.needs_reupload 
                    ? 'bg-red-50 hover:bg-red-100 border-l-4 border-red-500' 
                    : 'hover:bg-slate-50'
                }`}
                data-testid={`ppt-item-${ppt.id}`}
              >
                {/* Reupload Warning for this PPT */}
                {ppt.needs_reupload && (
                  <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2 text-red-700 text-sm">
                      <AlertTriangle size={16} />
                      <span>{ppt.unavailable_reason || 'File not available - needs reupload'}</span>
                    </div>
                    <button
                      onClick={() => setReuploadPpt(ppt)}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center gap-1"
                    >
                      <Upload size={14} />
                      Reupload Now
                    </button>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                    <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${
                      ppt.needs_reupload ? 'bg-red-200' : 'bg-blue-100'
                    }`}>
                      <FileText className={ppt.needs_reupload ? 'text-red-600' : 'text-blue-600'} size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className={`font-medium text-sm sm:text-base truncate ${
                        ppt.needs_reupload ? 'text-red-900' : 'text-slate-900'
                      }`}>{ppt.title}</h3>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <BookOpen size={12} className="sm:w-[14px] sm:h-[14px]" />
                          {ppt.subject_name || 'No subject'}
                        </span>
                        <span className="flex items-center gap-1">
                          <User size={12} className="sm:w-[14px] sm:h-[14px]" />
                          {ppt.teacher_name || 'No teacher'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-slate-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formatDateTime(ppt.created_at)}
                        </span>
                        {ppt.uploaded_by_name && (
                          <span className="flex items-center gap-1">
                            <span className="text-slate-300">•</span>
                            Uploaded by: <span className="text-slate-500">{ppt.uploaded_by_name}</span>
                          </span>
                        )}
                      </div>
                      {ppt.topic && (
                        <p className="text-xs sm:text-sm text-slate-600 mt-1 truncate">Topic: {ppt.topic}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pl-8 sm:pl-0">
                    <span className="text-xs text-slate-400 mr-1 sm:mr-2">
                      {formatFileSize(ppt.file_size)}
                    </span>
                    
                    <button
                      onClick={() => handleDownload(ppt)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title="Download"
                      data-testid={`download-ppt-${ppt.id}`}
                    >
                      <Download size={18} />
                    </button>
                    
                    {(isManager || (isAgent && ppt.created_by === user?.id)) && (
                      <button
                        onClick={() => handleDelete(ppt.id)}
                        disabled={deleting === ppt.id}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete"
                        data-testid={`delete-ppt-${ppt.id}`}
                      >
                        {deleting === ppt.id ? (
                          <Loader2 className="animate-spin" size={18} />
                        ) : (
                          <Trash2 size={18} />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination Controls */}
        {pagination && pagination.total_pages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total_count)} of {pagination.total_count} PPTs
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={!pagination.has_prev}
                className="px-3 py-1 text-sm border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={!pagination.has_prev}
                className="px-3 py-1 text-sm border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg">
                {pagination.page} / {pagination.total_pages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(pagination.total_pages, p + 1))}
                disabled={!pagination.has_next}
                className="px-3 py-1 text-sm border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(pagination.total_pages)}
                disabled={!pagination.has_next}
                className="px-3 py-1 text-sm border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">Upload PPT</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setTopicSearch('');
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleUpload} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter PPT title"
                  required
                  data-testid="upload-title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <select
                  value={uploadForm.subject_id}
                  onChange={(e) => {
                    setUploadForm({...uploadForm, subject_id: e.target.value, topic: ''});
                    setTopicSearch('');
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  data-testid="upload-subject"
                >
                  <option value="">Select Subject</option>
                  {subjects.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Teacher <span className="text-red-500">*</span>
                </label>
                <select
                  value={uploadForm.teacher_id}
                  onChange={(e) => setUploadForm({...uploadForm, teacher_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  data-testid="upload-teacher"
                >
                  <option value="">Select Teacher</option>
                  {teachers.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Topic <span className="text-red-500">*</span>
                </label>
                <input
                  ref={topicInputRef}
                  type="text"
                  value={topicSearch}
                  onChange={(e) => {
                    setTopicSearch(e.target.value);
                    setUploadForm({...uploadForm, topic: e.target.value});
                    setShowTopicDropdown(true);
                  }}
                  onFocus={() => setShowTopicDropdown(true)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Type to search or add topic..."
                  required
                  data-testid="upload-topic"
                />
                
                {/* Topic Dropdown */}
                {showTopicDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredTopics.length > 0 ? (
                      <>
                        {filteredTopics.map(topic => (
                          <button
                            key={topic.id}
                            type="button"
                            onClick={() => handleTopicSelect(topic)}
                            className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center justify-between text-sm"
                          >
                            <span>{topic.name}</span>
                            {topic.subject_name && (
                              <span className="text-xs text-slate-400">{topic.subject_name}</span>
                            )}
                          </button>
                        ))}
                      </>
                    ) : (
                      <div className="px-3 py-2 text-sm text-slate-500">
                        No matching topics found
                      </div>
                    )}
                    
                    {/* Add new topic option */}
                    {topicSearch.trim() && !filteredTopics.some(t => t.name.toLowerCase() === topicSearch.toLowerCase()) && (
                      <button
                        type="button"
                        onClick={handleAddNewTopic}
                        disabled={addingTopic}
                        className="w-full px-3 py-2 text-left bg-blue-50 hover:bg-blue-100 text-blue-700 flex items-center gap-2 text-sm border-t border-slate-200"
                      >
                        {addingTopic ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Plus size={14} />
                        )}
                        <span>Add "{topicSearch.trim()}" as new topic</span>
                      </button>
                    )}
                  </div>
                )}
                
                {/* Close dropdown when clicking outside */}
                {showTopicDropdown && (
                  <div 
                    className="fixed inset-0 z-0" 
                    onClick={() => setShowTopicDropdown(false)}
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter description (optional)"
                  rows={2}
                  data-testid="upload-description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  File <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
                  {uploadForm.file ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{uploadForm.file.name}</span>
                      <button
                        type="button"
                        onClick={() => setUploadForm({...uploadForm, file: null})}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".ppt,.pptx,.pdf"
                        onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                        className="hidden"
                        data-testid="upload-file"
                      />
                      <div className="text-slate-500">
                        <Upload className="mx-auto mb-2" size={24} />
                        <p className="text-sm">Click to select file</p>
                        <p className="text-xs text-slate-400 mt-1">PPT, PPTX, or PDF (max 50MB)</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  data-testid="submit-upload"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Upload
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default ClassTyping;
